import React, { useEffect, useRef, useState } from 'react';
import { HashRouter as Router, Route, Link } from 'react-router-dom';
import { getQuestionScore, getRealMaxScore, getQuizMaxScore, validQuiz, getQuizScore, getMostAnswer } from '../lib/qz';
import { getVerified, baseFake } from '../lib/ep.js';
import { Provider } from 'react-redux';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import Question from '../components/front/quiz/Question';
import Summary from '../components/front/quiz/Summary';
import ActionLink from '../components/builder/ActionLink';

const TRANSITION_DURATION = 500;	// ms

const ProgressQuizBar = ({ actual, total, ...props }) => {
	if(total == 1) return null;

	const enable_change = props?.enable_change ?? true;

	const navigate = (e, page) => {
		if(props.navigate != undefined) props.navigate(page, e);
	};

	return (
		<div class="quiz_progress gui_main_text_color_on_bg">
			<span class="navigation">
				{(enable_change) && (
					<ActionLink
						disabled={(actual < 2) ? true : false}
						onClick={(e) => navigate(e, actual-2)}
						icon='knk-arrow-l-big'
						_class={{ other:'gui_small_icon go_prev navigate_btn' }}
					/>
				)}

				<span class="page">{actual} / {total}</span>

				{(enable_change) && (
					<ActionLink
						disabled={(actual == total || !props.next_is_answered) ? true : false}
						onClick={(e) => navigate(e, actual)}
						icon='knk-arrow-r-big'
						_class={{ other:'gui_small_icon go_next navigate_btn' }}
					/>
				)}
			</span>

			<div class="progress">
				<span style={{width:`${100 * (actual) / total}%`}} class="bar" />
			</div>
		</div>
	)
};

const QuizClient = (props) => {
	const promotion = props?.promotion?.Promotion ?? (props?.promotion ?? {});
	const transition_axis = promotion.config?.transition_axis ?? 'h';
	const is_builder = props?.is_builder ?? false;

	const [ index, setIndex ] = useState(0);
	const [ locked, setLocked ] = useState({});
	const [ show_feedback, setShowFeedback ] = useState(false);
	const [ show_summary, setShowSummary ] = useState(false);
	const [ transition_direction, setTransitionDirection ] = useState('next');
	const [ user, setUser ] = useState({});

	const max_points = useRef(0);
	const points = useRef(0);

	const enable_change = props?.enable_change ?? true;
	const change_answers = (enable_change && (props.change_answers ?? true)) ? true : false;

	let quiz_score = false;
	if(props.stage != undefined) {
		quiz_score = (parseInt(props.stage?.Stage?.quiz_score_type ?? '0') == 1) ? true : false;
	} else {
		quiz_score = ((promotion?.quiz_score_type ?? 1) == 1) ? true : false;
	}

	if(!validQuiz(props.questions)) submitQuiz();

	const reset = () => {
		setUser({});
		setIndex(0);
		setShowFeedback(false);
		setLocked({});
		setShowSummary(false);
	};

	const userActionCb = (q, question_id) => {
		const _user = { ...user, [question_id]:q };
		setUser(user => ({...user,[question_id]:q}));

		if(props.clientAction != undefined) {
			props.clientAction(_user);
		}

		return _user;
	};

	/**
	 * Behaviour of what happens when a user clicks or interacts with an answer
	 * @param  {[type]} question [description]
	 * @param  {[type]} answer   [description]
	 * @return {[type]}          [description]
	 */
	const userAction = (question, answer, value) => {
		let _question = (user[question.id] != undefined) ? user[question.id] : {};

		// question is locked
		if(locked[question.id] != undefined) return false;

		// check the answer type and act acordingly
		if(['open_text', 'list', 'rating'].includes(answer.config.type)) {
			if(show_feedback) setShowFeedback(false);

			_question[answer.id] = value;

			return userActionCb(_question, question.id);
		}

		// the answer is not multianswer so we'll replace any answer
		if(!question.multianswer && question.is_advanced != 3) {
			if(question.is_advanced != "") _question = {};
			_question[answer.id] = 1;
		} else {
			// multianswer
			if(question.multiorder) {
				// check if the answer is already selected and clear higher ones
				if(_question[answer.id] != undefined) {
					const val = _question[answer.id];

					for(let i in _question) {
						// skip lower
						if(_question[i] < val) continue;
						delete _question[i];
					}
				} else {
					if(question.max_answers != null && question.numAnswers >= question.max_answers) return;

					let num = 1;
					for(let i in _question) num++;
					_question[answer.id] = num;
				}
			}

			if(!question.multiorder) {
				if(_question[answer.id] == undefined) {
					if(question.max_answers != null && question.numAnswers >= question.max_answers) return;
					_question[answer.id] = 1;
				} else {
					delete _question[answer.id];
				}
			}
		}

		let new_user = userActionCb(_question, question.id);
		// check if the question has a continue button
		nextQuestion(undefined,undefined,new_user);

		return new_user;
	};

	const continueAction = (cb) => {
		nextQuestion(true, cb);
	};

	const showSummary = (points) => {
		if(promotion.config.quiz_resume) {
			if(promotion.config.quiz_resume.show == '1') {
				return true;
			}

			if(promotion.config.quiz_resume.show == '2') {
				max_points.current = getQuizMaxScore(props.questions);

				//Check for wrong answers
				if(points < max_points.current) return true;
			}
		}

		return false;
	};

	const preventLeaving = (event) => {
		event.returnValue = "There are unsaved changes. Leave now?";
	};

	/**
	 * Try to go to the next question
	 */
	const nextQuestion = (next_question, cb,new_user) => {
		const q = props.questions[index];
		const can_continue = canContinue(q);

		// check if the question must be blocked
		if(next_question || q.Question.config.hidecontinue == true) {
			// if current question cannot be changed lock it
			if(!change_answers) {
				setLocked({ ...locked, [q.Question.id]:1 });
			}
		}

		// if there is feedback show it
		if(q.Question.feedback != null && q.Question.feedback != undefined && q.Question.feedback != "") {
			// we already displayed the feedback lets move on.
			if(next_question || q.Question.config.hidecontinue == true) {
				// if we haven't shown the feedback do it!
				if(can_continue && !show_feedback) {
					setShowFeedback(true);
					return;
				}
			} else {
				// there is no feedback so we continue
				setShowFeedback(false);
			}
		} else {
			// if we are hiding the continue button go to next
			if(q.Question.config.hidecontinue == true) next_question = true;
		}

		if(next_question) {
			// always report page
			const next_question_index = (index + 1);
			if(is_builder === false && window.ga != undefined) {
				const trackers = [];
				if(promotion.google_analytics_ua != '') trackers.push('newTracker');

				if(trackers.length > 0) {
					for(let i in trackers) {
						ga(`${trackers[i]}.set`, 'title', `${promotion.title} - Quiz ${next_question_index}`);
						ga(`${trackers[i]}.set`, 'page', `/quiz/${promotion.id}/${next_question_index}`);
						ga(`${trackers[i]}.send`, 'pageview');
					}
				}
			}

			// there's still questions
			if(next_question_index < props.questions.length) {
				//Move to the next one
				setShowFeedback(false);
				setIndex(next_question_index);
				setTransitionDirection('next');
			} else {
				// quiz has ended now display summary or go to somewhere else
				// if the promotion has the summary enabled display it.
				points.current = getQuizScore(props.questions, new_user ?? user);

				if(props.stage == undefined && showSummary(points.current)) {
					setShowSummary(true);
					return;
				}

				submitQuiz(cb,new_user);
			}

			window.scrollTo(0, 0);
		}
	};

	const submitQuiz = (cb,new_user) => {
		if(is_builder === false) window.removeEventListener('beforeunload', preventLeaving);

		let score = getQuizScore(props.questions, new_user ?? user, (quiz_score) ? 'majority' : 'sum');
		score = (quiz_score) ? getMostAnswer(score) : score;

		if(is_builder) props.quizFinished(score, new_user ?? user);

		if(props.preview) {
			const target = (promotion.hide_enter_button == 0) ? 'preview_form' : 'preview_success';
			self.location.href = self.location.href.replace('preview_quiz', target);
			return;
		}

		if(props.submitQuiz != undefined) {
			props.submitQuiz(score, new_user ?? user, cb);
			return;
		}

		$('#UserQuizResult').val(score);
		$('#UserQuizEntry').val(baseFake(getVerified(new_user ?? user)));
		$("form.fb").trigger('submit');
	};

	const questionClosed = (q) => {
		let num = 0;
		for(let i in q.Answer) if(q.Answer[i].config.type == 'closed_text') num++;
		return num;
	};

	const canContinue = (q) => {
		const question = q.Question;

		//Answers type select must be answered before we can continue
		const list_answers = q.Answer.filter((a) => a.config.type == 'list');
		for (var i in list_answers) {
			const answer = list_answers[i];
			if (user[question.id] == undefined || user[question.id][answer.id] == undefined || user[question.id][answer.id] == "-" ) {
				return false;
			}
		}

		//If there are no closed_answers you can always continue
		if(questionClosed(q) == 0) return true;

		var num = 0;
		for(var j in user[question.id]) num++;

		return ((num >= question.min_answers) && (question.max_answers == 0 || num <= question.max_answers || question.max_answers == null)) ? true : false;
	};

	const navigate = (new_index, e) => {
		e.preventDefault();
		setIndex(new_index);
		setTransitionDirection((new_index < index) ? 'prev' : 'next'); // check the current answer to determinate the direction
	};

	useEffect(() => {
		if(is_builder) return;
		window.addEventListener('beforeunload', preventLeaving);

		return () => {
			if(is_builder) return;
			window.removeEventListener('beforeunload', preventLeaving);
		}
	}, []);

	const question = props.questions[index];

	if(question == undefined) return null;

	let maxScore = getRealMaxScore(question);
	let userScore = getQuestionScore(question, user, true, ((quiz_score) ? 'majority' : 'sum'));
	const isRight = (userScore == maxScore) ? true : false;

	let num = 0;
	for(let j in user[question.Question.id]) num++;

	const can_continue = canContinue(question);

	const q = {
		...question,
		Question:{
			...question.Question,
			show_feedback,
			isRight,
			maxScore,
			numAnswers:num,
			canContinue:can_continue,
		},
		Answer:question.Answer.map((a, i) => {
			const extra = {};
			const q = question.Question;
			if(a.config.type == 'closed_text') {
				extra.selected = (user[a.question_id] != undefined && user[a.question_id][a.id] != undefined) ? true : false;
			} else {
				extra.value = (user[a.question_id] != undefined && user[a.question_id][a.id] != undefined) ? user[a.question_id][a.id] : '';
			}

			//Question is multiorder
			if(q.multiorder) {
				extra.order = (user[a.question_id] != undefined && user[a.question_id][a.id] != undefined) ? user[a.question_id][a.id] : 0;
			}

			return {...a, ...extra};
		})
	};

	if(show_summary) {
		const quiz_resume = promotion.config?.quiz_resume ?? {};
		const maxQuiz = getQuizMaxScore(props.questions);
		const userQuiz = getQuizScore(props.questions, user);

		return (
			<Summary
				promo={promotion}
				quiz_resume={quiz_resume}
				isRight={(maxQuiz == userQuiz) ? true : false}
				questions={props.questions}
				builder={false}
				user={user}
				onContinue={submitQuiz}
				onReset={reset}
				maxPoints={max_points.current}
			/>
		);
	}

	const progress_type = props?.progress_type ?? 'pagination';	// 'pagination' | 'bar'

	const actual_visible_question = props.questions[index]?.Question ?? {};
	const actual_visible_question_id = actual_visible_question?.id ?? false;
	let next_is_answered = (actual_visible_question_id !== false && user[actual_visible_question_id] != undefined) ? true : false;
	if(next_is_answered) {
		const min_answers_in_question = parseInt(actual_visible_question?.min_answers ?? '1');
		if(Object.keys(user[actual_visible_question_id]).length < min_answers_in_question) next_is_answered = false;
	}

	if(is_builder) {
		return (
			<div class="gui_flexbox gui_flex_col">
				{(progress_type == 'bar') && (
					<ProgressQuizBar
						actual={index + 1}
						navigate={navigate}
						total={props.questions.length}
						enable_change={enable_change}
						next_is_answered={(!is_builder) ? next_is_answered : null}
					/>
				)}

				<TransitionGroup
					className={`gui_transition_container slides_transition_to_${transition_direction}_${transition_axis}`}
				>
					<CSSTransition
						key={q.Question.id}
						classNames="slide"
						timeout={TRANSITION_DURATION}
					>
						<Question
							stage={props.stage}
							question={q}
							isMost={quiz_score}
							is_game={props.is_game}
							gp_hash={props.gp_hash}
							userAction={userAction}
							promo={promotion}
							continueAction={continueAction}
							user={user}
							resetQuiz={reset}
							multiparticipation={props.multiparticipation}
							canPlayAgain={props.canPlayAgain}
							locale={props.locale}
							is_test={(is_builder) ? true : (props?.preview ?? false)}
						/>
					</CSSTransition>
				</TransitionGroup>

				{(progress_type == 'pagination' && props.questions.length > 1) && (
					<ul class="gui_paginate">
						{props.questions.map((q, i) => {
							const selected = (i == index) ? true : false;
							const can_navigate = (enable_change && change_answers && (selected || user[q.Question.id] != undefined)) ? true : false;

							return (
								<li key={i} class={`hb_secondaryFont${(selected) ? ' selected' : ''}`}>
									{(can_navigate) ? (
										<a onClick={(e) => navigate(i, e)}>{i + 1}</a>
									) : (
										<span>{i + 1}</span>
									)}
								</li>
							)
						})}
					</ul>
				)}
			</div>
		)
	}

	return (
		<Provider store={props.store}>
			<Router hashType="noslash">
				<Route path="/" render={(route) => {
					return (
						<div class="gui_flexbox gui_flex_col">
							{(progress_type == 'bar') && (
								<ProgressQuizBar
									actual={index + 1}
									navigate={navigate}
									total={props.questions.length}
									enable_change={enable_change}
									next_is_answered={(!is_builder) ? next_is_answered : null}
								/>
							)}

							<TransitionGroup
								className={`gui_transition_container slides_transition_to_${transition_direction}_${transition_axis}`}
							>
								<CSSTransition
									key={q.Question.id}
									classNames="slide"
									timeout={TRANSITION_DURATION}
								>
									<Question
										stage={props.stage}
										question={q}
										isMost={quiz_score}
										is_game={props.is_game}
										gp_hash={props.gp_hash}
										userAction={userAction}
										promo={promotion}
										continueAction={continueAction}
										user={user}
										resetQuiz={reset}
										multiparticipation={props.multiparticipation}
										canPlayAgain={props.canPlayAgain}
										locale={props.locale}
										is_test={(is_builder) ? true : (props?.preview ?? false)}
									/>
								</CSSTransition>
							</TransitionGroup>

							{(progress_type == 'pagination' && props.questions.length > 1) && (
								<ul class="gui_paginate">
									{props.questions.map((q, i) => {
										const selected = (i == index) ? true : false;
										const can_navigate = (enable_change && change_answers && (selected || user[q.Question.id] != undefined)) ? true : false;

										return (
											<li key={i} class={`hb_secondaryFont${(selected) ? ' selected' : ''}`}>
												{(can_navigate) ? (
													<a onClick={(e) => navigate(i, e)}>{i + 1}</a>
												) : (
													<span>{i + 1}</span>
												)}
											</li>
										)
									})}
								</ul>
							)}
						</div>
					)
				}} />
			</Router>
		</Provider>
	)
};

export default QuizClient;